import LoanApplicationModal from "../../shared/LoanApplicationModal";
import { Eye, HelpCircle, Trash } from "lucide-react";
import { useQuery, useMutation } from "react-query";
import {
  deleteBorrowerChattel,
  getBorrowerChattels,
} from "../../sdk/borrower/borrower";
import { useBorrower } from "../../context/BorrowerContext";
import { useError } from "../../context/ErrorProvider";
import { useState } from "react";
import { useFormatAmount } from "../../hooks/useFormatAmount";

const BorrowerChattels = ({
  closeChattels,
  activeIndex,
  handleNextStep,
  handleAddChattel,
  loading,
}) => {
  const { borrower } = useBorrower();
  const { showError } = useError();
  const [expandedChattelId, setExpandedChattelId] = useState(null);
  const formatAmount = useFormatAmount();

  const { data: chattels, refetch: refetchChattels } = useQuery({
    queryKey: ["chattels", borrower?.id],
    queryFn: () => getBorrowerChattels(borrower?.id),
    initialData: [],
    enabled: !!borrower?.id,
  });

  const { mutate: deleteMyChattel } = useMutation({
    mutationKey: ["delete a chattel"],
    mutationFn: (id) => deleteBorrowerChattel(id),
    onSuccess: () => {
      refetchChattels();
    },
    onError: (errors) => {
      showError(errors?.response?.data?.message || errors.message);
    },
  });

  const handleDeleteChattel = (chattel) => {
    deleteMyChattel(chattel?.id);
  };

  const toggleExpand = (id) => {
    setExpandedChattelId((prev) => (prev === id ? null : id));
  };

  return (
    <LoanApplicationModal
      closeBioInformation={closeChattels}
      activeIndex={activeIndex}
      handleNextStep={handleNextStep}
      loading={loading}
    >
      <div className="flex items-end justify-between">
        <div>
          <p className="text-[#000000] font-normal text-[14px]">
            Create a New Loan Application
          </p>
          <p className="text-[#000000] font-semibold text-[20px]">
            Borrower’s Chattels
          </p>
          <p className="text-[#5C5C5C] text-[14px] font-normal">
            Record borrower’s chattels to be tied to this loan
          </p>
        </div>
        <button
          onClick={handleAddChattel}
          className="h-[35px] bg-[#000000] rounded-[30px] px-[15px] text-[15px] font-bold text-white"
        >
          Add A Chattel
        </button>
      </div>

      {/* Header */}
      <div className="h-[40px] w-full bg-[#F3F3F3] mt-[20px] px-[10px] flex items-center">
        <div className="w-[15%] text-[#222222] font-normal text-[14px]">ID</div>
        <div className="w-[30%] text-[#222222] font-normal text-[14px]">
          NAME
        </div>
        <div className="w-[27%] text-[#222222] font-normal text-[14px]">
          CHATTEL LOCATION
        </div>
        <div className="w-[24%] truncate text-[#222222] font-normal text-[14px]">
          VIEW / DELETE
        </div>
      </div>

      {/* Rows */}
      {chattels?.length === 0 ? (
        <div className="h-[350px] flex flex-col items-center justify-center gap-[10px]">
          <HelpCircle />
          <p>No chattel information available</p>
        </div>
      ) : (
        chattels?.map((item) => (
          <div key={item?.id}>
            {/* Row */}
            <div className="h-[40px] w-full bg-white px-[10px] flex items-center">
              <div className="w-[15%] text-[#222222] text-[14px]">
                {item?.public_id}
              </div>
              <div className="w-[30%] text-[#00A676] text-[14px]">
                {item?.name}
              </div>
              <div className="w-[27%] text-[#222222] text-[14px]">
                {item?.location}
              </div>
              <div className="w-[24%] flex gap-3 items-center">
                <Eye
                  className="w-4 h-4 text-[#222222] cursor-pointer"
                  onClick={() => toggleExpand(item?.id)}
                />
                <Trash
                  onClick={() => handleDeleteChattel(item)}
                  className="w-4 h-4 text-[#222222] cursor-pointer"
                />
              </div>
            </div>

            {/* Expanded Details */}
            {expandedChattelId === item?.id && (
              <div className="bg-[#F9F9F9] px-4 py-3 text-sm text-[#333]">
                <p className="font-bold">Chattel details</p>
                <p>
                  <span className="font-semibold">Name:</span>{" "}
                  {item?.name || "N/A"}
                </p>
                <p>
                  <span className="font-semibold">Value:</span>{" "}
                  {formatAmount(item?.value ?? 0) || "N/A"}
                </p>
                <p>
                  <span className="font-semibold">Description:</span>{" "}
                  {item?.description || "N/A"}
                </p>
                <p>
                  <span className="font-semibold">Location:</span>{" "}
                  {item?.location}
                </p>
                <p>
                  <span className="font-semibold">Condition:</span>{" "}
                  {item?.condition}
                </p>
              </div>
            )}
          </div>
        ))
      )}
    </LoanApplicationModal>
  );
};

export default BorrowerChattels;
